import React, { useEffect, useState } from 'react';
import VdoPlayer from './VdoPlayer.tsx';
import { Grid, Badge } from '@radix-ui/themes';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios.js';
import './LessonList.css';

interface Lesson {
    id: number;
    name: string;
    description: string;
    vdo_file: string;
    order?: number;
}

interface ExamStatus {
    hasTakenExam: boolean;
    score: number;
    fullScore: number;
    isRetest: boolean;
}

const LessonList = () => {
    const navigate = useNavigate();
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [examStatuses, setExamStatuses] = useState<{ [key: number]: ExamStatus }>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    useEffect(() => {
        fetchLessons();
    }, [user.id]);

    const fetchLessons = async () => {
        try {
            const response = await api.get(`/allLessons`);
            // Sort lessons by order if it exists, otherwise by ID
            const sortedLessons = response.data.sort((a, b) => {
                if (a.order !== undefined && b.order !== undefined) {
                    return a.order - b.order;
                }
                return a.id - b.id;
            });
            setLessons(sortedLessons);

            // Fetch exam status for each lesson
            if (user.id) {
                const statuses = await Promise.all(
                    sortedLessons.map(async (lesson: Lesson) => {
                        const statusRes = await api.get(
                            `/exams/check/${lesson.id}/${user.id}`
                        );
                        return { lessonId: lesson.id, status: statusRes.data };
                    })
                );

                const statusMap = statuses.reduce((acc, { lessonId, status }) => {
                    acc[lessonId] = status;
                    return acc;
                }, {});

                setExamStatuses(statusMap);
            }
        } catch (error) {
            console.error('Error fetching lessons:', error);
        }
    };

    const handleLessonClick = (lessonId) => {
        navigate(`/lesson/${lessonId}`);
    };

    const getScoreColor = (score: number, fullScore: number) => {
        const percentage = score / fullScore;
        return percentage < 0.7 ? 'red' : 'green';
    };

    const formatScore = (score: number, fullScore: number) => {
        const percentage = (score / fullScore) * 100;
        return `${score}/${fullScore} (${percentage.toFixed(0)}%)`;
    };

    return (
        <div className="lesson-list">
            <Grid columns="3" gap="4">
                {lessons.map((lesson) => (
                    <div 
                        key={lesson.id} 
                        className={`lesson-list-item ${examStatuses[lesson.id]?.hasTakenExam ? 'exam-taken' : ''}`}
                        onClick={() => handleLessonClick(lesson.id)}
                    >
                        <div className="video-container">
                            <VdoPlayer videoUrl={lesson.vdo_file} />
                            {examStatuses[lesson.id]?.hasTakenExam && (
                                <div className="exam-status">
                                    <Badge
                                        className="exam-badge"
                                        color={getScoreColor(
                                            examStatuses[lesson.id].score,
                                            examStatuses[lesson.id].fullScore
                                        )}
                                    >
                                        {formatScore(
                                            examStatuses[lesson.id].score,
                                            examStatuses[lesson.id].fullScore
                                        )}
                                    </Badge>
                                    {examStatuses[lesson.id].isRetest && (
                                        <Badge className="retest-badge" color="blue">
                                            สอบซ่อมได้
                                        </Badge>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="lesson-list-content">
                            <h3>{lesson.name}</h3>
                            <p>{lesson.description}</p>
                        </div>
                    </div>
                ))}
            </Grid>
        </div>
    );
};

export default LessonList;